import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { AreaLineChartComponent, AreaLineChartDataPoint, AreaLineChartSeries, AreaLineChartLegendItem, AreaLineChartFillBetween } from '../../../../../_shared/components/graphs/area-line-chart/area-line-chart.component';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { MaintenanceInsightsResponse } from '../../../models/cost-tracking-response.model';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { formatCost, formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { MaintenanceTrendModalComponent } from '../../modals/maintenance-trend-modal/maintenance-trend-modal.component';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { formatCurrencyAmount } from '../../../../../_shared/helpers/currency/currency-format.helper';
import { TranslocoService } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../../_shared/helpers/widget-filter.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';

@Component({
  selector: 'app-maintenance-trend',
  standalone: true,
  imports: [CommonModule, AreaLineChartComponent, GraphTrendIconComponent, EmptyStateComponent],
  templateUrl: './maintenance-trend.component.html',
  styleUrl: './maintenance-trend.component.scss'
})
export class MaintenanceTrendComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private cancelSubject: Subject<void> = new Subject<void>();
  private authService = inject(AuthService);

  title: string = inject(TranslocoService).translate('costTracking.charts.maintenance');
  isLoading: boolean = true;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  chartData: AreaLineChartDataPoint[] = [];
  readonly chartHeight = 165;
  yTicks: number[] = [100, 80, 60, 40, 20, 0];

  // Updated series with new colors:
  // - 'You' is a line (no fill - the fill is for ROI between You and Benchmark)
  // - 'Benchmark' is a straight line (lineType: 'straight')
  // ROI is represented as the shaded area between You and Benchmark (via fillBetween)
  readonly series: AreaLineChartSeries[] = [
    { key: 'you', color: '#3873C9', label: inject(TranslocoService).translate('costTracking.trend.you'), fillOpacity: 0 },
    { key: 'benchmark', color: '#FF7F00', label: inject(TranslocoService).translate('costTracking.trend.benchmark'), fillOpacity: 0, lineType: 'straight' }
  ];

  // ROI is the shaded area between benchmark (top) and you (bottom)
  readonly roiFillBetween: AreaLineChartFillBetween = {
    topKey: 'benchmark',
    bottomKey: 'you',
    color: '#66AECF',
    fillOpacity: 0.4,
    label: inject(TranslocoService).translate('costTracking.trend.roi')
  };

  readonly legend: AreaLineChartLegendItem[] = [
    { legend: this.transloco.translate('costTracking.trend.you'), color: '#3873C9' },
    { legend: 'ROI', color: '#66AECF' },
    { legend: this.transloco.translate('costTracking.trend.benchmark'), color: '#FF7F00' }
  ];

  totalValue: number = 0;
  percentageChange: number = 5.12;

  private filterSubscription: Subscription;

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe(async (filter) => {
      this.widgetFilter = filter;
      await this.loadData();
    });

    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    void this.loadData();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
      };

      // Fetch summary, you, and benchmark data in parallel
      // ROI is not a separate data series - it's the shaded area between You and Benchmark
      const [summaryResponse, youResponse, benchmarkResponse] = await Promise.all([
        this.costTrackingService.getMaintenanceSummary(request, this.cancelSubject),
        this.costTrackingService.getMaintenanceYouInsights(request, this.cancelSubject),
        this.costTrackingService.getMaintenanceBenchmarkValue(request, this.cancelSubject)
      ]);

      const youData = youResponse.data ?? [];
      const benchmarkValue = benchmarkResponse.data ?? 0;
      const summary = summaryResponse.data;

      // Process responses and group data
      this.chartData = this.processResponses(youData, benchmarkValue);
      this.calculateYTicks(this.chartData, benchmarkValue);
      this.percentageChange = summary?.percentageChange ?? 0;
      this.totalValue = summary?.currentValue ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.isLoading = false;
        }
      });
    }
  }

  private processResponses(
    youData: MaintenanceInsightsResponse[],
    benchmarkValue: number
  ): AreaLineChartDataPoint[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { data: youData, label: 'you' }
    ];

    let groupedData: GroupedChartData[];
    switch (groupingType) {
      case 'hourly':
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!, 7);
        break;
      default:
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    // Add benchmark as a constant value to each data point (straight line)
    return groupedData.map(item => ({
      ...item,
      label: item['label'] || item['dateRange'],
      benchmark: benchmarkValue
    }));
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: AreaLineChartDataPoint[], benchmarkValue: number): void {
    if (!chartData || chartData.length === 0) return;

    const allValues = chartData.flatMap(item => [
      Number(item['you']) || 0,
      benchmarkValue
    ]);

    const maxValue = Math.max(...allValues);
    const roundedMax = Math.ceil(maxValue / 100000) * 100000 || 100000;

    this.yTicks = [];
    for (let i = 5; i >= 0; i--) {
      this.yTicks.push(Math.round((roundedMax / 5) * i));
    }
    this.yTicks = this.yTicks.reverse();
  }

  formatYAxis = (value: number): string => {
    return formatCost(value, this.currencySymbol);
  }

  formatTooltip = (value: number): string => {
    return formatCurrencyAmount(this.currencySymbol, value);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  isUptrendPositive(): boolean {
    return true;
  }

  hasData(): boolean {
    return this.chartData.length > 0 && this.chartData.some(item => {
      return (Number(item['you']) || 0) > 0;
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(MaintenanceTrendModalComponent, modalOptions);
  }
}